import { chassisArtFor, type ChassisArt, type Olt, type OltFamily } from '@simpleacs/shared';

/**
 * A drawing of the chassis, for every OLT nobody has photographed.
 *
 * These are APPROXIMATIONS and the page says so wherever one is shown. No
 * vendor photography is traced, copied or fetched — nothing here loads
 * anything over the network — and no vendor mark appears on any of them. What
 * they are built from is each family's `basis` in `@simpleacs/shared`, and
 * every detail that is specific rather than generic comes from something this
 * platform has actually read off the operator's own chassis: the C300's shelf
 * enumerating slots 0 to 20 with its fan tray keyed by name instead of by
 * slot; the C650's four-unit fan group and its two power modules in slots 10
 * and 12; the X17's eight boards with a paired active/standby control; the
 * MA5683T's shorter subrack that registers no environment unit at all. Where a
 * count is not established it is not drawn as a count.
 *
 * Everything is painted from the theme's own custom properties rather than
 * fixed hex, so a drawing follows the light/dark toggle without a second copy
 * of itself. `currentColor` is deliberately not used for the body: these sit
 * inside text containers, and inheriting the running text's colour would make
 * a chassis the same weight as a sentence.
 */

const BODY = 'var(--surface-2)';
const EDGE = 'var(--border-strong)';
const BAY = 'var(--surface)';
const BAY_EMPTY = 'var(--surface-3)';
const DETAIL = 'var(--border)';
const HANDLE = 'var(--text-faint)';
const LED = 'var(--success)';
const LABEL = 'var(--text-faint)';

interface ArtProps {
  /**
   * Slot numbers and unit markings. Off for the thumbnail, where 9px text is
   * an illegible smear that reads as dirt on the drawing rather than as
   * information, and on at full size where it is the point.
   */
  labels: boolean;
}

/** One service card: a face, a puller handle, and a status light. */
function Card({
  x,
  y,
  w,
  h,
  populated = true,
  led = LED,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  populated?: boolean;
  led?: string;
}) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={1.5}
        fill={populated ? BAY : BAY_EMPTY}
        stroke={DETAIL}
        strokeWidth={0.8}
      />
      {populated && (
        <>
          {/* The ejector handle, which is what makes a card face read as a
              card face rather than as a rectangle. */}
          <rect x={x + w / 2 - 2} y={y + h - 9} width={4} height={6} rx={1} fill={HANDLE} />
          <circle cx={x + w / 2} cy={y + 5} r={1.4} fill={led} />
        </>
      )}
    </g>
  );
}

/** A horizontal board: same idea, laid the other way. */
function Board({
  x,
  y,
  w,
  h,
  populated = true,
  led = LED,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  populated?: boolean;
  led?: string;
}) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={1.5}
        fill={populated ? BAY : BAY_EMPTY}
        stroke={DETAIL}
        strokeWidth={0.8}
      />
      {populated && (
        <>
          <rect x={x + 4} y={y + h / 2 - 2} width={7} height={4} rx={1} fill={HANDLE} />
          <circle cx={x + w - 8} cy={y + h / 2} r={1.6} fill={led} />
          <line
            x1={x + 18}
            y1={y + h / 2}
            x2={x + w - 16}
            y2={y + h / 2}
            stroke={DETAIL}
            strokeWidth={0.8}
          />
        </>
      )}
    </g>
  );
}

/** A fan unit. `lit` false draws the blades with no indicator at all. */
function Fan({ cx, cy, r, lit = true }: { cx: number; cy: number; r: number; lit?: boolean }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={BAY_EMPTY} stroke={DETAIL} strokeWidth={0.8} />
      <circle cx={cx} cy={cy} r={r * 0.22} fill={HANDLE} />
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <line
          key={deg}
          x1={cx}
          y1={cy}
          x2={cx + Math.cos((deg * Math.PI) / 180) * r * 0.82}
          y2={cy + Math.sin((deg * Math.PI) / 180) * r * 0.82}
          stroke={DETAIL}
          strokeWidth={0.9}
        />
      ))}
      {lit && <circle cx={cx} cy={cy + r + 4} r={1.3} fill={LED} />}
    </g>
  );
}

/** The 19-inch mounting ears every one of these hangs from. */
function Ears({ x1, x2, y, h }: { x1: number; x2: number; y: number; h: number }) {
  return (
    <g>
      {[x1, x2].map((x) => (
        <g key={x}>
          <rect x={x - 6} y={y} width={7} height={h} rx={1} fill={BODY} stroke={EDGE} strokeWidth={1} />
          {[y + 8, y + h / 2, y + h - 8].map((cy) => (
            <circle key={cy} cx={x - 2.5} cy={cy} r={1.4} fill={DETAIL} />
          ))}
        </g>
      ))}
    </g>
  );
}

/**
 * ZTE ZXA10 C300 — one shelf, cards standing on edge across it, slots 0 to 20.
 *
 * The fan tray is drawn beside the shelf rather than in it because that is how
 * the chassis itself answers: `show card-power` prints every card against a
 * rack/shelf/slot and then prints `FAN(FCWB)` with no slot columns at all.
 */
function C300Art({ labels }: ArtProps) {
  const slots = 21;
  const left = 34;
  const right = 330;
  const pitch = (right - left) / slots;
  return (
    <>
      <rect x={26} y={34} width={348} height={132} rx={4} fill={BODY} stroke={EDGE} strokeWidth={1.5} />
      <Ears x1={26} x2={380} y={34} h={132} />
      {Array.from({ length: slots }, (_, i) => (
        <Card
          key={i}
          x={left + i * pitch}
          y={48}
          w={pitch - 2.5}
          h={104}
          // Slot 4 is drawn empty: it is the one the C300 prints with no card
          // type and every reading as `N/A.`, and a shelf with one gap in it
          // is what a real shelf looks like.
          populated={i !== 4}
        />
      ))}
      <rect x={336} y={44} width={34} height={112} rx={2} fill={BAY_EMPTY} stroke={DETAIL} strokeWidth={0.8} />
      <Fan cx={353} cy={72} r={11} />
      <Fan cx={353} cy={126} r={11} />
      {labels && (
        <g fontSize={8} fill={LABEL} fontFamily="ui-monospace, monospace">
          <text x={left} y={44} textAnchor="start">
            0
          </text>
          <text x={right - 4} y={44} textAnchor="end">
            20
          </text>
          <text x={353} y={100} textAnchor="middle">
            FAN
          </text>
        </g>
      )}
    </>
  );
}

/**
 * ZTE ZXA10 C650 — a taller shelf, a four-unit fan group along the bottom, and
 * two power modules sharing the chassis's DC budget.
 *
 * The bays are drawn without numbers on purpose. The fans and the power slots
 * are things this platform has read; how many service cards a C650 shelf takes
 * is not, and a drawing that asserted a number would be teaching an operator
 * something about their own rack that nobody checked.
 */
function C650Art({ labels }: ArtProps) {
  const slots = 14;
  const left = 34;
  const right = 300;
  const pitch = (right - left) / slots;
  return (
    <>
      <rect x={26} y={22} width={348} height={156} rx={4} fill={BODY} stroke={EDGE} strokeWidth={1.5} />
      <Ears x1={26} x2={380} y={22} h={156} />
      {Array.from({ length: slots }, (_, i) => (
        <Card key={i} x={left + i * pitch} y={34} w={pitch - 3} h={104} />
      ))}
      {/* Two power modules, the pair the chassis reports as slots 10 and 12. */}
      <Card x={308} y={34} w={26} h={104} />
      <Card x={340} y={34} w={26} h={104} />
      {/* The fan group: four units, indices 0 to 3. */}
      <rect x={34} y={144} width={332} height={26} rx={2} fill={BAY_EMPTY} stroke={DETAIL} strokeWidth={0.8} />
      {[0, 1, 2, 3].map((i) => (
        <Fan key={i} cx={70 + i * 86} cy={157} r={9} lit={false} />
      ))}
      {labels && (
        <g fontSize={8} fill={LABEL} fontFamily="ui-monospace, monospace">
          <text x={321} y={30} textAnchor="middle">
            PWR
          </text>
          <text x={353} y={30} textAnchor="middle">
            PWR
          </text>
          {[0, 1, 2, 3].map((i) => (
            <text key={i} x={70 + i * 86 + 16} y={160} textAnchor="start">
              {i}
            </text>
          ))}
        </g>
      )}
    </>
  );
}

/**
 * Huawei MA5800-X17 — a tall subrack of horizontal boards, eight of them, with
 * the control pair adjacent and a fan unit that is its own environment unit.
 *
 * The paired control is the detail worth drawing: `display board` shows two
 * H902MPLA in neighbouring slots, one `Active_normal` and one
 * `Standby_normal`, which is a redundancy an operator can see at a glance in a
 * picture and cannot in a table.
 */
function Ma5800Art({ labels }: ArtProps) {
  const boards = 8;
  const top = 26;
  const pitch = 18;
  // Stops short of the fan bay so the slot markings have somewhere to sit that
  // is not on top of a board face.
  const width = 216;
  return (
    <>
      <rect x={26} y={16} width={348} height={168} rx={4} fill={BODY} stroke={EDGE} strokeWidth={1.5} />
      <Ears x1={26} x2={380} y={16} h={168} />
      {Array.from({ length: boards }, (_, i) => (
        <Board
          key={i}
          x={40}
          y={top + i * pitch}
          w={width}
          h={pitch - 4}
          // The two in the middle are the control pair; the last is the power
          // board. Everything else is a service board.
          led={i === 4 || i === 5 ? 'var(--info)' : i === boards - 1 ? 'var(--warn)' : LED}
        />
      ))}
      {/* The bracket that says those two are one function, not two cards. */}
      <path
        d={`M ${34} ${top + 4 * pitch} L ${30} ${top + 4 * pitch} L ${30} ${top + 6 * pitch - 4} L ${34} ${top + 6 * pitch - 4}`}
        fill="none"
        stroke={HANDLE}
        strokeWidth={1}
      />
      {/* The fan EMU: registered in its own right, and reporting a real draw. */}
      <rect x={300} y={26} width={62} height={144} rx={2} fill={BAY_EMPTY} stroke={DETAIL} strokeWidth={0.8} />
      <Fan cx={331} cy={60} r={17} />
      <Fan cx={331} cy={130} r={17} />
      {labels && (
        <g fontSize={8} fill={LABEL} fontFamily="ui-monospace, monospace">
          <text x={294} y={top + 4 * pitch + 9} textAnchor="end">
            MPU
          </text>
          <text x={294} y={top + 5 * pitch + 9} textAnchor="end">
            MPU
          </text>
          <text x={294} y={top + 7 * pitch + 9} textAnchor="end">
            PWR
          </text>
          <text x={331} y={100} textAnchor="middle">
            FAN
          </text>
        </g>
      )}
    </>
  );
}

/**
 * Huawei MA5683T — the shorter subrack of the two, service boards low and the
 * control above them.
 *
 * Its fan area carries no indicator, and that is the honest detail rather than
 * an oversight: this chassis registers no environment unit at all (`Used or
 * not : Not used`, 0 W), so a lit LED here would be claiming a reading that
 * nothing is taking.
 */
function Ma5683Art({ labels }: ArtProps) {
  const boards = 6;
  const top = 44;
  const pitch = 19;
  return (
    <>
      <rect x={26} y={34} width={348} height={132} rx={4} fill={BODY} stroke={EDGE} strokeWidth={1.5} />
      <Ears x1={26} x2={380} y={34} h={132} />
      {Array.from({ length: boards }, (_, i) => (
        <Board
          key={i}
          x={40}
          y={top + i * pitch}
          // Stops short of the fan bay, leaving the markings a clear lane.
          w={200}
          h={pitch - 5}
          led={i === 0 ? 'var(--info)' : LED}
        />
      ))}
      <rect x={280} y={44} width={82} height={112} rx={2} fill={BAY_EMPTY} stroke={DETAIL} strokeWidth={0.8} />
      <Fan cx={303} cy={100} r={16} lit={false} />
      <Fan cx={340} cy={100} r={16} lit={false} />
      {labels && (
        <g fontSize={8} fill={LABEL} fontFamily="ui-monospace, monospace">
          <text x={274} y={top + 10} textAnchor="end">
            CTRL
          </text>
          <text x={321} y={140} textAnchor="middle">
            FAN — unmonitored
          </text>
        </g>
      )}
    </>
  );
}

/**
 * Anything else. A rack chassis and nothing more specific: this OLT's model
 * did not match a family, so implying a slot layout would be inventing one.
 */
function GenericArt({ labels }: ArtProps) {
  const boards = 5;
  const top = 48;
  const pitch = 21;
  return (
    <>
      <rect x={26} y={38} width={348} height={124} rx={4} fill={BODY} stroke={EDGE} strokeWidth={1.5} />
      <Ears x1={26} x2={380} y={38} h={124} />
      {Array.from({ length: boards }, (_, i) => (
        <Board key={i} x={40} y={top + i * pitch} w={244} h={pitch - 6} />
      ))}
      <rect x={296} y={48} width={66} height={104} rx={2} fill={BAY_EMPTY} stroke={DETAIL} strokeWidth={0.8} />
      <Fan cx={329} cy={78} r={15} />
      <Fan cx={329} cy={122} r={15} />
      {labels && (
        <g fontSize={8} fill={LABEL} fontFamily="ui-monospace, monospace">
          <text x={200} y={30} textAnchor="middle">
            model not recognised
          </text>
        </g>
      )}
    </>
  );
}

/**
 * The registry, typed by family.
 *
 * `Record<OltFamily, …>` rather than a lookup with a fallback: adding a family
 * to `OltFamily` in shared then fails this typecheck until it has a drawing,
 * which is the only way "every family has artwork" stays true without anybody
 * remembering to check.
 */
const ART: Record<OltFamily, (props: ArtProps) => React.JSX.Element> = {
  c3xx: C300Art,
  c6xx: C650Art,
  ma58xx: Ma5800Art,
  ma56xx: Ma5683Art,
  unknown: GenericArt,
};

/** The drawing for an OLT, and the description that goes with it. */
export function artForOlt(olt: Pick<Olt, 'vendor' | 'model'>): ChassisArt {
  return chassisArtFor(olt.vendor, olt.model);
}

export function ChassisArtwork({
  olt,
  labels = false,
  className,
}: {
  olt: Pick<Olt, 'vendor' | 'model'>;
  labels?: boolean;
  className?: string;
}) {
  const art = artForOlt(olt);
  const Drawing = ART[art.family];
  return (
    <svg
      viewBox="0 0 400 200"
      preserveAspectRatio="xMidYMid meet"
      className={className}
      role="img"
      aria-label={`Drawing of a ${art.label} chassis — an approximation, not a photograph`}
    >
      <Drawing labels={labels} />
    </svg>
  );
}
